import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Upload, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GetStartedForm = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    date: new Date().toISOString().split('T')[0],
    firstName: '',
    middleName: '',
    lastName: '',
    hebrewName: '',
    address: '',
    phone: '',
    whatsapp: '',
    bestWayToReach: [],
    email: '',
    age: '',
    birthDateEnglish: '',
    birthDateHebrew: '',
    specificIssue: '',
    currentIllnesses: '',
    
    // Step 2: Background
    career: '',
    religiousAffiliation: '',
    namedAfter: '',
    namedAfterDetails: '',
    maritalStatus: '',
    marriageYears: '',
    firstMarriage: '',
    children: '',
    childrenDetails: '',
    parentsAlive: '',
    parentsDetails: '',
    siblings: '',
    siblingsDetails: '',
    
    // Step 3: Medical & Emotional
    familyMedicalIssues: '',
    familyMedicalDetails: '',
    diagnosedIssues: '',
    diagnosedDetails: '',
    operations: '',
    operationsDetails: '',
    traumas: '',
    traumaDetails: '',
    emotionalState: [],
    medication: '',
    medicationDetails: '',
    vitaminDeficiencies: '',
    vitaminDetails: '',
    previousTreatment: '',
    previousTreatmentDetails: '',
    
    // Step 4: Personal Growth
    motivationForChange: '',
    biggestChallenges: '',
    supportSystem: '',
    supportDetails: '',
    selfCare: [],
    selfCareDetails: '',
    energyTherapyFamiliarity: '',
    discoveryGoals: '',
    favoriteColor: '',
    howDiscovered: '',
    additionalServices: [],
    additionalQuestions: '',
    
    // Step 5: Photo
    photo: null,
    photoPreview: '',
    photoId: null,
    
    // Step 6: Contracts
    consentAgreed: false,
    waiverAgreed: false,
    clientSignature: '',
    signatureDate: new Date().toISOString().split('T')[0],
    
    // Step 7: Payment
    paymentConfirmed: false
  });

  const totalSteps = 7;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      updateField('photo', file);
      updateField('photoPreview', reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to backend
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/uploads/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('photoId', data.photo_id);
      toast.success('Photo uploaded successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed. Please try again.');
      updateField('photo', null);
      updateField('photoPreview', '');
      updateField('photoId', null);
    } finally {
      setUploading(false);
    }
  };

  const validateStep = () => {
    switch(currentStep) {
      case 1:
        if (!formData.firstName || !formData.lastName || !formData.hebrewName || !formData.age || !formData.specificIssue || !formData.currentIllnesses) {
          toast.error('Please fill in all mandatory fields');
          return false;
        }
        return true;
      case 5:
        if (!formData.photo || !formData.photoId) {
          toast.error('Please upload a full-length photo');
          return false;
        }
        if (uploading) {
          toast.error('Photo is still uploading. Please wait.');
          return false;
        }
        return true;
      case 6:
        if (!formData.consentAgreed || !formData.waiverAgreed || !formData.clientSignature) {
          toast.error('Please agree to all terms and provide your signature');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    try {
      setSubmitting(true);
      const payload = { ...formData };
      delete payload.photo;
      delete payload.photoPreview;
      payload.photo_id = formData.photoId;

      const submissionResp = await axios.post(`${API}/submissions`, payload);
      const submissionId = submissionResp.data.submission_id;
      toast.success('Application saved! Redirecting to secure payment...');

      const checkoutResp = await axios.post(`${API}/checkout/session`, {
        submission_id: submissionId,
        package_id: 'six_session',
        origin_url: window.location.origin,
      });

      if (checkoutResp.data.url) {
        window.location.href = checkoutResp.data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="form-step">
            <h2 className="heading-2">Basic Information</h2>
            <p className="body-small text-muted">Fields marked with * are mandatory</p>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input type="text" className="form-input" value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input type="text" className="form-input" value={formData.middleName} onChange={(e) => updateField('middleName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input type="text" className="form-input" value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} required />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Hebrew Name (your full name ben/bat your mother's full name) *</label>
              <input type="text" className="form-input" value={formData.hebrewName} onChange={(e) => updateField('hebrewName', e.target.value)} required />
            </div>

            <div className="form-group">
              <label className="form-label">Exact Address (State/City, Country, Zip Code)</label>
              <textarea className="form-textarea" rows="2" value={formData.address} onChange={(e) => updateField('address', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input type="tel" className="form-input" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp Number</label>
                <input type="tel" className="form-input" value={formData.whatsapp} onChange={(e) => updateField('whatsapp', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Best Way to Reach You</label>
              <div className="checkbox-group">
                {['Phone', 'Email', 'WhatsApp'].map(method => (
                  <label key={method} className="checkbox-label">
                    <input type="checkbox" checked={formData.bestWayToReach.includes(method)} onChange={() => toggleArrayField('bestWayToReach', method)} />
                    <span>{method}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input type="email" className="form-input" value={formData.email} onChange={(e) => updateField('email', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Age *</label>
                <input type="number" className="form-input" value={formData.age} onChange={(e) => updateField('age', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth (English)</label>
                <input type="date" className="form-input" value={formData.birthDateEnglish} onChange={(e) => updateField('birthDateEnglish', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth (Hebrew)</label>
                <input type="text" className="form-input" placeholder="e.g., 15 Shevat 5745" value={formData.birthDateHebrew} onChange={(e) => updateField('birthDateHebrew', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Identify one specific issue you want addressed *</label>
              <p className="caption">This will serve as the overall intention for the session</p>
              <textarea className="form-textarea" rows="4" value={formData.specificIssue} onChange={(e) => updateField('specificIssue', e.target.value)} required />
            </div>

            <div className="form-group">
              <label className="form-label">Are you currently suffering from any illnesses? *</label>
              <textarea className="form-textarea" rows="3" value={formData.currentIllnesses} onChange={(e) => updateField('currentIllnesses', e.target.value)} required />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="form-step">
            <h2 className="heading-2">Background & Family</h2>
            
            <div className="form-group">
              <label className="form-label">Career/Current Employment</label>
              <input type="text" className="form-input" value={formData.career} onChange={(e) => updateField('career', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Religious Affiliation</label>
              <select className="form-input" value={formData.religiousAffiliation} onChange={(e) => updateField('religiousAffiliation', e.target.value)}>
                <option value="">Select...</option>
                <option value="Torah Observant/Religious">Torah Observant/Religious</option>
                <option value="Conservative">Conservative</option>
                <option value="Reform">Reform</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Are you named after someone?</label>
              <select className="form-input" value={formData.namedAfter} onChange={(e) => updateField('namedAfter', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.namedAfter === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Who? What do you know about that person?</label>
                <textarea className="form-textarea" rows="3" value={formData.namedAfterDetails} onChange={(e) => updateField('namedAfterDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Marital Status</label>
              <select className="form-input" value={formData.maritalStatus} onChange={(e) => updateField('maritalStatus', e.target.value)}>
                <option value="">Select...</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widow">Widow</option>
              </select>
            </div>

            {formData.maritalStatus === 'Married' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">How many years married?</label>
                  <input type="text" className="form-input" value={formData.marriageYears} onChange={(e) => updateField('marriageYears', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">First marriage?</label>
                  <select className="form-input" value={formData.firstMarriage} onChange={(e) => updateField('firstMarriage', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Do you have any children or stepchildren?</label>
              <select className="form-input" value={formData.children} onChange={(e) => updateField('children', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.children === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list their names and ages</label>
                <textarea className="form-textarea" rows="3" value={formData.childrenDetails} onChange={(e) => updateField('childrenDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Are your parents alive?</label>
              <select className="form-input" value={formData.parentsAlive} onChange={(e) => updateField('parentsAlive', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.parentsAlive === 'No' && (
              <div className="form-group">
                <label className="form-label">How long ago did they pass away?</label>
                <input type="text" className="form-input" value={formData.parentsDetails} onChange={(e) => updateField('parentsDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Do you have siblings?</label>
              <select className="form-input" value={formData.siblings} onChange={(e) => updateField('siblings', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.siblings === 'Yes' && (
              <div className="form-group">
                <label className="form-label">How many brothers/sisters? Please list their ages</label>
                <textarea className="form-textarea" rows="2" value={formData.siblingsDetails} onChange={(e) => updateField('siblingsDetails', e.target.value)} />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="form-step">
            <h2 className="heading-2">Medical & Emotional History</h2>
            
            <div className="form-group">
              <label className="form-label">Any medical issues (physical/emotional) in your family?</label>
              <select className="form-input" value={formData.familyMedicalIssues} onChange={(e) => updateField('familyMedicalIssues', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.familyMedicalIssues === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list the condition and who it is affecting</label>
                <textarea className="form-textarea" rows="3" value={formData.familyMedicalDetails} onChange={(e) => updateField('familyMedicalDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Have you been diagnosed with any medical/emotional/mental issues?</label>
              <select className="form-input" value={formData.diagnosedIssues} onChange={(e) => updateField('diagnosedIssues', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.diagnosedIssues === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list details and date of diagnosis</label>
                <textarea className="form-textarea" rows="3" value={formData.diagnosedDetails} onChange={(e) => updateField('diagnosedDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Any previous operations or hospitalizations?</label>
              <select className="form-input" value={formData.operations} onChange={(e) => updateField('operations', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.operations === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list details including dates</label>
                <textarea className="form-textarea" rows="3" value={formData.operationsDetails} onChange={(e) => updateField('operationsDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Are there any traumas that had a significant impact on your life?</label>
              <select className="form-input" value={formData.traumas} onChange={(e) => updateField('traumas', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.traumas === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list the trauma(s), date and your age when it occurred</label>
                <textarea className="form-textarea" rows="4" value={formData.traumaDetails} onChange={(e) => updateField('traumaDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">What moods describe your overall emotional state? (Select all that apply)</label>
              <div className="checkbox-group-multi">
                {['Depressed', 'Calm', 'Lazy', 'Hyper', 'Anxious', 'Angry', 'Sad', 'Frustrated', 'Unstable', 'Unmotivated', 'Lost', 'Confused', 'Traumatized', 'Repressed', 'Detached', 'Panic'].map(mood => (
                  <label key={mood} className="checkbox-label">
                    <input type="checkbox" checked={formData.emotionalState.includes(mood)} onChange={() => toggleArrayField('emotionalState', mood)} />
                    <span>{mood}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Are you taking any medication?</label>
              <select className="form-input" value={formData.medication} onChange={(e) => updateField('medication', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.medication === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list names and daily dosage</label>
                <textarea className="form-textarea" rows="3" value={formData.medicationDetails} onChange={(e) => updateField('medicationDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Do you have any vitamin/mineral deficiencies?</label>
              <select className="form-input" value={formData.vitaminDeficiencies} onChange={(e) => updateField('vitaminDeficiencies', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.vitaminDeficiencies === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please list</label>
                <input type="text" className="form-input" value={formData.vitaminDetails} onChange={(e) => updateField('vitaminDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Have you previously been in counseling or medical treatment?</label>
              <select className="form-input" value={formData.previousTreatment} onChange={(e) => updateField('previousTreatment', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.previousTreatment === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Please explain</label>
                <textarea className="form-textarea" rows="3" value={formData.previousTreatmentDetails} onChange={(e) => updateField('previousTreatmentDetails', e.target.value)} />
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="form-step">
            <h2 className="heading-2">Personal Growth & Energy Therapy</h2>
            
            <div className="form-group">
              <label className="form-label">What motivates you to want change in your life?</label>
              <textarea className="form-textarea" rows="3" value={formData.motivationForChange} onChange={(e) => updateField('motivationForChange', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">What are your biggest challenges?</label>
              <textarea className="form-textarea" rows="3" value={formData.biggestChallenges} onChange={(e) => updateField('biggestChallenges', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Do you have support from friends and/or family?</label>
              <select className="form-input" value={formData.supportSystem} onChange={(e) => updateField('supportSystem', e.target.value)}>
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            {formData.supportSystem === 'Yes' && (
              <div className="form-group">
                <label className="form-label">Who offers support and how is it helpful?</label>
                <textarea className="form-textarea" rows="3" value={formData.supportDetails} onChange={(e) => updateField('supportDetails', e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">How do you practice self-care? (Select all that apply)</label>
              <div className="checkbox-group-multi">
                {['Meditation', 'Yoga', 'Healing sessions', 'Time alone', 'Time in nature', 'Social activities', 'Short get-aways', 'Vacations', 'Reading', 'Studying', 'Exercise'].map(activity => (
                  <label key={activity} className="checkbox-label">
                    <input type="checkbox" checked={formData.selfCare.includes(activity)} onChange={() => toggleArrayField('selfCare', activity)} />
                    <span>{activity}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">How explain how these are helpful</label>
              <textarea className="form-textarea" rows="2" value={formData.selfCareDetails} onChange={(e) => updateField('selfCareDetails', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">How familiar are you with energy therapy?</label>
              <select className="form-input" value={formData.energyTherapyFamiliarity} onChange={(e) => updateField('energyTherapyFamiliarity', e.target.value)}>
                <option value="">Select...</option>
                <option value="Previously used">Previously used</option>
                <option value="Been aware but never used">Been aware but never used</option>
                <option value="Just heard recently">Just heard recently</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">What might you be wanting to discover about yourself through energy therapy?</label>
              <textarea className="form-textarea" rows="4" value={formData.discoveryGoals} onChange={(e) => updateField('discoveryGoals', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">What is your favorite color?</label>
              <input type="text" className="form-input" value={formData.favoriteColor} onChange={(e) => updateField('favoriteColor', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">How did you discover Chanah's Energy Therapy?</label>
              <textarea className="form-textarea" rows="2" value={formData.howDiscovered} onChange={(e) => updateField('howDiscovered', e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Additional services you'd like to explore (Select all that apply)</label>
              <div className="checkbox-group-multi">
                {['EFT Tapping', 'Narrative Therapy', 'Integrative Nutrition Health Coaching', 'Plant Based Cooking Demonstrations', 'Personal Growth Retreat'].map(service => (
                  <label key={service} className="checkbox-label">
                    <input type="checkbox" checked={formData.additionalServices.includes(service)} onChange={() => toggleArrayField('additionalServices', service)} />
                    <span>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Any other questions/concerns important for me to know?</label>
              <textarea className="form-textarea" rows="4" value={formData.additionalQuestions} onChange={(e) => updateField('additionalQuestions', e.target.value)} />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="form-step">
            <h2 className="heading-2">Photo Upload</h2>
            <p className="body-medium">Please upload a full-length photo from the last 3 months of the person receiving energy work.</p>
            
            <div className="upload-area">
              <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
              {!formData.photoPreview ? (
                <label htmlFor="photo-upload" className="upload-label">
                  <Upload size={48} />
                  <p className="body-medium">Click to upload photo</p>
                  <p className="caption">Max file size: 5MB</p>
                </label>
              ) : (
                <div className="photo-preview">
                  <img src={formData.photoPreview} alt="Preview" />
                  <label htmlFor="photo-upload" className="btn-secondary btn-sm">Change Photo</label>
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="form-step">
            <h2 className="heading-2">Consent & Waiver</h2>
            
            <div className="contract-section">
              <h3 className="heading-3">Energy Therapy Consent</h3>
              <div className="contract-text">
                <p className="body-small">
                  Energy Therapy is a non-local form of treatment that relates to the mind, body and soul as one unit. It involves identifying and reducing/eliminating the root causes of energetic blockages. These blockages often manifest as the unwanted physical, mental or emotional issues that surface in one's life.
                </p>
                <p className="body-small">
                  Once a person's energetic fields have been opened and cleared, energy is free to flow through a person in its natural state. This is the essence of the healing process. Hashem is the source of this healing; I am merely a vessel used to open the channels for Light to flow more freely into the client's life.
                </p>
                <p className="body-small">
                  Sessions typically take about an hour to complete. After the energy session I will send an audio recording of the comprehensive findings. An assessment of the recommended number of additional sessions will be determined at the conclusion of the first session.
                </p>
                <p className="body-small"><strong>Payment:</strong> Payment must be received no later than 7 days prior to the scheduling of the initial session.</p>
                <p className="body-small"><strong>Cancellation Policy:</strong> There is a $75 cancellation fee if you opt to cancel a scheduled session. Rescheduling, cancellations, or withdrawals must be done no less than 2 weeks in advance for there to be no cancellation charge.</p>
              </div>
              <label className="checkbox-label-block">
                <input type="checkbox" checked={formData.consentAgreed} onChange={(e) => updateField('consentAgreed', e.target.checked)} />
                <span>I have read and agree to the Energy Therapy Consent</span>
              </label>
            </div>

            <div className="contract-section">
              <h3 className="heading-3">Waiver of Liability</h3>
              <div className="contract-text">
                <p className="body-small">
                  While the techniques of this therapy are completely energy-based, specific exercises involving meditation, acupressure, crystals, etc. may be recommended. Although energy therapy is expected to produce beneficial results, such results cannot be guaranteed.
                </p>
                <p className="body-small">
                  Energy Therapy is a complement, not a substitute for, medical or psychological treatment. Any ongoing treatment should not be discontinued without the advice of your physician.
                </p>
                <p className="body-small">
                  Adverse side effects are rare for most clients. Some temporary side effects may include: an increase of fatigue, anxiety, sadness, frustration or other symptoms. From an energy medicine perspective, side effects are viewed as potential confirmation that a core issue has been uncovered.
                </p>
                <p className="body-small">
                  Chanah Rothberg is not licensed to diagnose, treat, cure, mitigate or prevent any medical or psychological disease or disorder.
                </p>
                <p className="body-small"><strong>CONFIDENTIALITY:</strong> Client information will be kept in confidence and will not be disclosed to anyone outside of this office without your written consent, unless as required by law.</p>
              </div>
              <label className="checkbox-label-block">
                <input type="checkbox" checked={formData.waiverAgreed} onChange={(e) => updateField('waiverAgreed', e.target.checked)} />
                <span>I have read and agree to the Waiver of Liability</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Your Signature (Type your full name) *</label>
              <input type="text" className="form-input" value={formData.clientSignature} onChange={(e) => updateField('clientSignature', e.target.value)} placeholder="Type your full name as signature" required />
              <p className="caption">Your typing is considered a signature</p>
            </div>

            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={formData.signatureDate} onChange={(e) => updateField('signatureDate', e.target.value)} readOnly />
            </div>
          </div>
        );

      case 7:
        return (
          <div className="form-step">
            <h2 className="heading-2">Secure Payment</h2>
            <div className="payment-info">
              <div className="price-card">
                <h3 className="heading-3">6 Session Package</h3>
                <div className="price">3,570 NIS</div>
                <ul className="price-features">
                  <li className="body-small">Initial comprehensive assessment</li>
                  <li className="body-small">5 follow-up sessions</li>
                  <li className="body-small">Audio reports after each session</li>
                  <li className="body-small">Tools and exercises for healing</li>
                  <li className="body-small">WhatsApp support between sessions</li>
                </ul>
              </div>

              <div className="payment-placeholder">
                <p className="body-medium text-center">
                  Click <strong>Submit & Pay</strong> below to complete your application and securely pay through PayPal (card or PayPal account). After payment, Chanah will reach out to you via WhatsApp to schedule your first session.
                </p>
                <div className="form-group">
                  <label className="checkbox-label-block">
                    <input
                      type="checkbox"
                      checked={formData.paymentConfirmed}
                      onChange={(e) => updateField('paymentConfirmed', e.target.checked)}
                      data-testid="payment-confirm-checkbox"
                    />
                    <span>I understand that my submission is final and I am ready to complete payment now</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="form-overlay">
      <div className="form-container">
        <div className="form-header">
          <div>
            <h1 className="heading-2">Begin Your Healing Journey</h1>
            <p className="body-small text-muted">Step {currentStep} of {totalSteps}</p>
          </div>
          <button onClick={onClose} className="btn-close" data-testid="form-close-btn">
            <X size={24} />
          </button>
        </div>

        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }} />
        </div>

        <div className="form-content">
          {renderStep()}
        </div>

        <div className="form-footer">
          {currentStep > 1 && (
            <button onClick={prevStep} className="btn-secondary" data-testid="form-prev-btn" disabled={submitting}>
              <ChevronLeft size={20} />
              <span>Previous</span>
            </button>
          )}
          <div className="flex-spacer" />
          {currentStep < totalSteps ? (
            <button onClick={nextStep} className="btn-primary" data-testid="form-next-btn" disabled={uploading}>
              <span>{uploading ? 'Uploading...' : 'Next'}</span>
              <ChevronRight size={20} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="btn-primary"
              data-testid="form-submit-pay-btn"
              disabled={submitting || !formData.paymentConfirmed}
            >
              <CreditCard size={20} />
              <span>{submitting ? 'Processing...' : 'Submit & Pay 3,570 NIS'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetStartedForm;
